import React from 'react';
import PropTypes from 'prop-types';

import { logError, logInfo } from '../../utils/logs';
import { useAuth } from '../../contexts/auth_context';

export function ErrorBoundaryWrapper({ children, fallbackMessage, onError }) {
	const { errorMessage } = useAuth();
	return (
		<ErrorBoundary fallbackMessage={errorMessage || fallbackMessage} onError={onError}>
			{children}
		</ErrorBoundary>
	);
}

ErrorBoundaryWrapper.propTypes = {
	children: PropTypes.node.isRequired,
	fallbackMessage: PropTypes.string,
	onError: PropTypes.func,
};

export class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		};
	}

	static getDerivedStateFromError(_error) {
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		logError('React Error Boundary caught an error', error, {
			componentStack: errorInfo.componentStack,
			errorBoundary: this.constructor.name,
			userAgent: navigator.userAgent,
			url: window.location.href,
			timestamp: new Date().toISOString(),
		});

		logInfo('Error boundary activated, showing fallback UI', {
			hasFallbackMessage: !!this.props.fallbackMessage,
			hasOnError: !!this.props.onError,
		});

		this.setState({
			error: error,
			errorInfo: errorInfo,
		});
	}

	handleRetry = () => {
		logInfo('User attempting to retry after error', {
			previousError: this.state.error?.message,
			timestamp: new Date().toISOString(),
		});

		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		});
	};

	render() {
		if (this.state.hasError) {
			return (
				<div
					className='error-boundary bg-red-50 border border-red-200 rounded-lg p-6 m-4'
					data-testid='error-boundary'
					role='alert'
					aria-live='polite'
				>
					<div className='flex items-center mb-4'>
						<div className='flex-shrink-0 text-red-500'>
							<svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
								<path
									fillRule='evenodd'
									d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
									clipRule='evenodd'
								/>
							</svg>
						</div>
						<div className='ml-3'>
							<h3 className='text-sm font-medium text-red-800'>
								{this.props.fallbackMessage || 'Something went wrong.'}
							</h3>
						</div>
					</div>
					{this.state.errorInfo && (
						<details className='text-sm text-red-700 mt-2'>
							<summary className='font-semibold cursor-pointer'>
								Error Details (Development Only)
							</summary>
							<div className='mt-2 p-3 bg-red-100 rounded border'>
								<pre className='text-sm text-red-800 whitespace-pre-wrap'>
									{this.state.error.toString()}
									{this.state.errorInfo.componentStack}
								</pre>
							</div>
						</details>
					)}

					<div className='flex space-x-3'>
						<button
							onClick={this.handleRetry}
							className='bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500'
							data-testid='retry-button'
						>
							Try Again
						</button>

						{this.props.onError && (
							<button
								onClick={() =>
									this.props.onError(this.state.error, this.state.errorInfo)
								}
								className='bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500'
								data-testid='report-button'
							>
								Report Problem
							</button>
						)}
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired,
	fallbackMessage: PropTypes.string,
	onError: PropTypes.func,
};
